import {
  BaseButton,
  BaseCard,
  BaseDeleteButton,
  BaseDialog,
  BaseIcon,
  createFieldHelper,
  renderFieldHelperArrayInput,
  SelectOption,
  useHookForm
} from '@cks-internal/react-base';
import {
  NewUnverifiedTravellerModel,
  NewUnverifiedTravellerRequest
} from '../../../features/activities/travellers/types';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { Fieldset, Heading, HStack, Stack, Text, UseDisclosureReturn } from '@chakra-ui/react';
import { useNewUnverifiedTravellerMutation } from '../../../features/activities/travellers/api/useNewUnverifiedTravellerMutation';
import { useActivityQueryInvalidator } from '../../../features/activities/editActivity/useActivityQueryInvalidator';
import { Dispatch, useEffect, useState } from 'react';
import { TravellerNameCheckResponse, useTravellerNameCheckMutation } from './api/useTravellerNameCheck';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { SelectedTravellerPill } from '../../../features/activities/travellers/selectedTravellerPill';
import { nameRegex, nameValidationMessage } from '../../../features/composeModal/constants';

type Props = {
  companyId: number;
  disclosure: UseDisclosureReturn;
  onCreated: (newOptions: SelectOption[]) => void;
  activityId?: number;

  noDrag?: boolean;
};

export const NewTravellerDialog = ({ ...props }: Props) => {
  const { formMethods } = useHookForm<NewUnverifiedTravellerRequest>({ Travellers: [{ FirstName: '', LastName: '' }] });
  const fieldArray = useFieldArray({
    control: formMethods.control,
    name: 'Travellers'
  });

  const activityQueryInvalidator = useActivityQueryInvalidator(props.activityId ?? -1);
  const fieldHelper = createFieldHelper<NewUnverifiedTravellerModel, { Travellers: NewUnverifiedTravellerModel[] }>(
    formMethods
  );
  const fields = [
    fieldHelper.textInput('FirstName').withValidation({
      required: { message: 'First Name is required.' },
      pattern: { value: nameRegex, message: nameValidationMessage }
    }),
    fieldHelper.textInput('LastName').withValidation({
      required: { message: 'Last Name is required.' },
      pattern: { value: nameRegex, message: nameValidationMessage }
    })
  ];

  const [matches, setMatches] = useState<TravellerNameCheckResponse[]>([]);

  useEffect(() => {
    matches.forEach((m, i) => {
      if (m?.IsError) {
        formMethods.setError(`Travellers.${i}`, { message: m?.Message ?? '' });
      }
    });
  }, [matches]);
  return (
    <BaseDialog
      size={'md'}
      dragDialog={
        props.noDrag
          ? undefined
          : {
              initialH: 600
            }
      }
      disclosure={props.disclosure}
      footerRender={
        <HStack justifyContent={'flex-end'} gap={2}>
          <BaseButton
            variant={'outline'}
            onClick={() => {
              props.disclosure.onClose();
              formMethods.reset({ Travellers: [{ FirstName: '', LastName: '' }] });
              setMatches([]);
            }}>
            Cancel
          </BaseButton>
          <SubmitButton
            formMethods={formMethods}
            matches={matches}
            setMatches={setMatches}
            companyId={props.companyId}
            activityId={props.activityId}
            onSubmit={async (data) => {
              props.disclosure.onClose();
              await activityQueryInvalidator.invalidateAllQueriesForActivity();
              props.onCreated(data);
            }}
          />
        </HStack>
      }
      header={'Add New Travellers'}>
      <BaseCard
        variant='fieldArray'
        header={
          <HStack justifyContent={'space-between'}>
            <Heading>Unverified Traveller Names </Heading>
            <BaseIcon
              icon={faAdd}
              cursor={'pointer'}
              onClick={() => {
                fieldArray.append({ FirstName: '', LastName: '' });
              }}
            />
          </HStack>
        }>
        {fieldArray.fields.map((field, i) => {
          return (
            <Fieldset.Root
              p={4}
              key={field.id}
              invalid={matches[i]?.IsError || formMethods.formState.errors.Travellers?.[i] != undefined}>
              <Stack>
                <HStack>
                  {renderFieldHelperArrayInput('FirstName', 'Travellers', `1-${field.id}`, i, fields)}
                  {renderFieldHelperArrayInput('LastName', 'Travellers', `2-${field.id}`, i, fields)}
                  <BaseDeleteButton
                    ml={'auto'}
                    cursor={'pointer'}
                    color={'#ff2121'}
                    onClick={() => {
                      setMatches((o) => o.toSpliced(i, 1));
                      fieldArray.remove(i);
                    }}
                  />
                </HStack>
                <Fieldset.ErrorText>
                  {formMethods.formState.errors.Travellers?.[i]?.LastName?.message}
                  {formMethods.formState.errors.Travellers?.[i]?.FirstName?.message}
                  {formMethods.formState.errors.Travellers?.[i]?.message}
                </Fieldset.ErrorText>
                {matches[i] && (
                  <>
                    {!matches[i].IsError && <Text fontStyle={'italic'}>{matches[i].Message}</Text>}
                    <HStack wrap={'wrap'}>
                      {matches[i].Options.map((o, i) => {
                        return (
                          <BaseButton
                            key={i}
                            toolTip={o.DescriptionLines?.join(', ')}
                            disabled={
                              o.IsDisabled || !matches[i]?.IsError ? (matches[i]?.Message ?? 'cannot add') : undefined
                            }
                            onClick={() => {
                              props.onCreated([o]);
                              setMatches((o) => o.toSpliced(i, 1));
                              if (fieldArray.fields.length == 1) {
                                props.disclosure.onClose();
                                formMethods.setValue('Travellers', []);
                                return;
                              }
                              fieldArray.remove(i);
                            }}
                            bgColor={'white'}
                            p={2}
                            variant={'ghost'}
                            borderRadius={'16px'}>
                            <SelectedTravellerPill selectOption={o} activityId={props.activityId} showContactType />
                          </BaseButton>
                        );
                      })}
                    </HStack>
                  </>
                )}
              </Stack>
            </Fieldset.Root>
          );
        })}
      </BaseCard>
    </BaseDialog>
  );
};

type SubmitButtonProps = {
  formMethods: UseFormReturn<NewUnverifiedTravellerRequest>;
  matches: TravellerNameCheckResponse[];
  setMatches: Dispatch<TravellerNameCheckResponse[]>;
  companyId: number;
  activityId: number | undefined;
  onSubmit: (d: SelectOption[]) => Promise<void>;
};
const SubmitButton = (props: SubmitButtonProps) => {
  const trav = useWatch({ control: props.formMethods.control, name: 'Travellers' });
  const createMutation = useNewUnverifiedTravellerMutation();
  const checkMutation = useTravellerNameCheckMutation();
  const [doCheck, setDoCheck] = useState(true);
  useEffect(() => {
    setDoCheck(true);
  }, [trav]);
  const isDisabled = (props.formMethods.formState.errors.Travellers?.length ?? 0) > 0;
  return (
    <BaseButton
      toolTip={isDisabled ? 'Please correct the errors before submitting.' : undefined}
      disabled={isDisabled}
      isLoading={createMutation.isPending || checkMutation.isPending}
      onClick={() => {
        props.formMethods.handleSubmit(async (data) => {
          const check = await checkMutation.mutateAsync({
            companyId: props.companyId,
            model: data,
            activityId: props.activityId
          });
          if (check.length != 0 && doCheck) {
            props.setMatches(check);
            setDoCheck(false);
            return;
          }
          const toReturn = await createMutation.mutateAsync({
            companyId: props.companyId,
            activityId: props.activityId,
            model: data
          });
          await props.onSubmit(toReturn.data);
          props.formMethods.setValue('Travellers', []);
        })();
      }}>
      {props.formMethods.formState.isSubmitted && !doCheck ? 'Confirm' : 'Save'}
    </BaseButton>
  );
};
